import { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useRoomStore, useGuestStore, useUserStore } from '@/stores'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { MessageSquare, User, Users, Calendar, Clock } from 'lucide-react'
import { RoomOpen, RoomClose } from '@core/models'
import type { Guest, User as UserType } from '@core/models'
import { Skeleton } from '@/components/ui/skeleton'
import { ChatMessage } from '@/components/ChatMessage'

const RoomDetail = observer(() => {
  const { tenantId, roomId } = useParams<{ tenantId: string; roomId: string }>()
  const roomStore = useRoomStore()
  const guestStore = useGuestStore()
  const userStore = useUserStore()
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [roomGuest, setRoomGuest] = useState<Guest | null>(null)
  const [roomUser, setRoomUser] = useState<UserType | null>(null)

  useEffect(() => {
    if (tenantId && roomId) {
      roomStore.getRoom(tenantId, roomId)
    }
  }, [tenantId, roomId, roomStore])

  // ルーム情報が取得されたら、ゲストとユーザーの情報を取得
  useEffect(() => {
    if (roomStore.currentRoom && tenantId) {
      // ゲスト情報を取得
      if (roomStore.currentRoom.guest) {
        guestStore.getGuest(tenantId, roomStore.currentRoom.guest).then(() => {
          const guest = guestStore.guests.get(roomStore.currentRoom!.guest!)
          if (guest) {
            setRoomGuest(guest)
          }
        })
      }
      // ユーザー情報を取得
      if (roomStore.currentRoom.user) {
        userStore.getUser(tenantId, roomStore.currentRoom.user).then(() => {
          const user = userStore.users.get(roomStore.currentRoom!.user!)
          if (user) {
            setRoomUser(user)
          }
        })
      }
    }
  }, [roomStore.currentRoom, tenantId, guestStore, userStore])

  // メッセージが更新されたら最下部にスクロール
  useEffect(() => {
    if (scrollAreaRef.current && roomStore.currentRoomMessages.length > 0) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight
    }
  }, [roomStore.currentRoomMessages])

  const room = roomStore.currentRoom

  if (!room) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-[200px]" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    )
  }

  const getStateLabel = (state: number) => {
    switch (state) {
      case RoomOpen:
        return 'オープン'
      case RoomClose:
        return 'クローズ'
      default:
        return '-'
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            ルーム詳細
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <div className="text-sm text-muted-foreground mb-1">ID</div>
                <div className="font-mono text-sm">{room.id}</div>
              </div>
              
              <div>
                <div className="text-sm text-muted-foreground mb-1">ルーム名</div>
                <div className="font-medium">{room.name}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-1">概要</div>
                <div>{room.description || '-'}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-1">状態</div>
                <Badge 
                  variant={room.state === RoomOpen ? 'default' : 'secondary'}
                >
                  {room.state !== undefined ? getStateLabel(room.state) : '-'}
                </Badge>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-sm text-muted-foreground mb-1">問い合わせゲスト</div>
                {room.guest ? (
                  <Link 
                    to={`/tenants/${tenantId}/guests/${room.guest}`}
                    className="text-primary hover:underline flex items-center gap-1"
                  >
                    <Users className="h-3 w-3" />
                    {roomGuest ? roomGuest.name : room.guest}
                  </Link>
                ) : (
                  <div>-</div>
                )}
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-1">対応ユーザー</div>
                {room.user ? (
                  <Link 
                    to={`/tenants/${tenantId}/users/${room.user}`}
                    className="text-primary hover:underline flex items-center gap-1"
                  >
                    <User className="h-3 w-3" />
                    {roomUser ? roomUser.name : room.user}
                  </Link>
                ) : (
                  <div>-</div>
                )}
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  開始日時
                </div>
                <div>
                  {room.created 
                    ? new Date(room.created).toLocaleString('ja-JP')
                    : '-'}
                </div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  更新日時
                </div>
                <div>
                  {room.updated 
                    ? new Date(room.updated).toLocaleString('ja-JP')
                    : '-'}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            メッセージ履歴
          </CardTitle>
        </CardHeader>
        <CardContent>
          {roomStore.currentRoomMessages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>メッセージがありません</p>
            </div>
          ) : (
            <div 
              ref={scrollAreaRef}
              className="h-[500px] overflow-y-auto pr-4 space-y-2"
            >
              {roomStore.currentRoomMessages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  guestName={message.guest === roomGuest?.id ? roomGuest.name : undefined}
                  userName={message.user === roomUser?.id ? roomUser.name : undefined}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
})

export default RoomDetail